<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core'

import { transactionService } from '~/api/service/transaction.service'
import TopFloatingButton from '~/components/@pages/transactions/TopFloatingButton.vue'
import DetailsBox from '~/components/box/DetailsBox.vue'
import PageTitleHeader from '~/components/header/PageTitleHeader.vue'
import Spinner from '~/components/loading/Spinner.vue'
import TransactionInfiniteTable from '~/components/table/transactions/TransactionInfiniteTable.vue'
import { TransactionModel } from '~/types/model/transaction.model'
import { ITransactionParam } from '~/types/transaction.type'
import { getBreadCrumb } from '~/utils/breadCrumbList'

const param = ref<ITransactionParam>({
  size: 100,
})

const { data: infiniteData, isInitialLoading, isFetching, hasNextPage, fetchNextPage } =
  transactionService.useGetTransactionList(param)

const loadingRef = ref<HTMLElement | null>(null)
useIntersectionObserver(loadingRef, ([ { isIntersecting } ]) => {
  if (isIntersecting && !isInitialLoading.value && !isFetching.value && hasNextPage?.value) {
    fetchNextPage()
  }
})

const totalCount = computed(() => infiniteData.value?.pages[0].formattedTotalCount ?? '0')

const rows = computed(() => {
  if (!infiniteData.value) {
    return []
  }
  return infiniteData.value?.pages.reduce((acc, cur) => {
    return acc.concat(cur.transactionList)
  }, Array<TransactionModel>())
})

useSeoMeta(getMeta('Transactions', 'View the latest transactions'))

useHead(getBreadCrumb())

definePageMeta({
  layout: 'transaction'
})
</script>

<template>
  <div class="transactions-page">
    <PageTitleHeader title="Transactions" />
    <DetailsBox>
      Showing all <span class="text-primary">{{ totalCount }}</span> results
    </DetailsBox>
    <TransactionInfiniteTable
      :rows="rows"
      :is-loading="isInitialLoading"
      :row-count="param.size"
    />
    <TopFloatingButton class="fixed bottom-28 right-6" />
    <div
      v-if="!isInitialLoading && hasNextPage"
      class="flex !h-28 items-center justify-center"
      ref="loadingRef"
    >
      <Spinner />
    </div>
  </div>
</template>

<style scoped>
.transactions-page {
  @apply w-full flex flex-col gap-y-4;
}
</style>